/**
 * <cart-cross-sell> : cross-sell du tiroir panier.
 *
 * - Cache les produits déjà présents dans le panier et limite l'affichage au
 *   nombre choisi dans l'éditeur de thème (data-max).
 * - Mélange l'ordre des produits au chargement et à chaque ouverture du tiroir
 *   (si l'option "Ordre aléatoire" est activée, data-shuffle).
 * - Ajoute un produit au panier via /cart/add.js puis demande au thème de
 *   rafraîchir le tiroir (événement "theme:cart:refresh").
 * - Se met à jour à chaque changement du panier ("theme:cart:change").
 */
(function () {
  if (customElements.get('cart-cross-sell')) return;

  var inflight = null;

  // Un seul appel /cart.js partagé entre les instances (latérale + intégrée).
  function fetchCart(url) {
    if (!inflight) {
      inflight = fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          setTimeout(function () {
            inflight = null;
          }, 0);
          return cart;
        })
        .catch(function (error) {
          inflight = null;
          throw error;
        });
    }
    return inflight;
  }

  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      this.max = parseInt(this.dataset.max, 10) || 4;
      this.shuffleEnabled = this.dataset.shuffle === 'true';
      this.addUrl = this.dataset.addUrl;
      this.cartUrl = this.dataset.cartUrl;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.sync = this.sync.bind(this);
      this.onDrawerOpen = this.onDrawerOpen.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('theme:cart:change', this.sync);
      document.addEventListener('theme:cart-drawer:open', this.onDrawerOpen);

      if (this.shuffleEnabled) this.shuffle();
      this.applyVisibility(this.productIdsFromDom());
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('theme:cart:change', this.sync);
      document.removeEventListener('theme:cart-drawer:open', this.onDrawerOpen);
    }

    get items() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-cross-sell-item]'));
    }

    // Nouvel ordre aléatoire (Fisher-Yates) : les cartes sont simplement déplacées dans la liste.
    shuffle() {
      var list = this.querySelector('[data-cross-sell-list]');
      if (!list) return;

      var items = this.items;
      for (var i = items.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = items[i];
        items[i] = items[j];
        items[j] = tmp;
      }

      items.forEach(function (item) {
        list.appendChild(item);
      });
    }

    onDrawerOpen() {
      if (this.shuffleEnabled) this.shuffle();
      this.sync();
    }

    // État initial : le serveur a déjà marqué "hidden" les produits présents dans le panier.
    productIdsFromDom() {
      return new Set(
        this.items
          .filter(function (item) {
            return item.hasAttribute('hidden');
          })
          .map(function (item) {
            return String(item.dataset.productId);
          })
      );
    }

    sync() {
      var self = this;
      fetchCart(this.cartUrl)
        .then(function (cart) {
          var ids = new Set(
            cart.items.map(function (line) {
              return String(line.product_id);
            })
          );
          self.applyVisibility(ids, cart.item_count);
        })
        .catch(function () {
          /* on garde l'affichage actuel */
        });
    }

    applyVisibility(idsInCart, itemCount) {
      var shown = 0;
      var max = this.max;

      this.items.forEach(function (item) {
        var inCart = idsInCart.has(String(item.dataset.productId));
        var hide = inCart || shown >= max;
        item.hidden = hide;
        if (!hide) shown += 1;
      });

      this.hidden = shown === 0 || itemCount === 0;
    }

    onChange(event) {
      var select = event.target.closest('[data-cross-sell-select]');
      if (!select) return;

      var item = select.closest('[data-cross-sell-item]');
      var option = select.options[select.selectedIndex];
      var price = item && item.querySelector('[data-cross-sell-price]');

      if (item) item.dataset.variantId = select.value;
      if (price && option && option.dataset.price) price.textContent = option.dataset.price;
    }

    onClick(event) {
      var button = event.target.closest('[data-cross-sell-add]');
      if (!button || button.disabled) return;

      var item = button.closest('[data-cross-sell-item]');
      var select = item.querySelector('[data-cross-sell-select]');
      var variantId = select ? select.value : item.dataset.variantId;
      if (!variantId) return;

      this.add(item, button, variantId);
    }

    add(item, button, variantId) {
      var error = item.querySelector('[data-cross-sell-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      button.disabled = true;
      item.classList.add('is-loading');

      fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            var message = result.data.description || result.data.message || '';
            if (error && message) {
              error.textContent = message;
              error.hidden = false;
            }
            return;
          }

          // Le thème recharge le contenu du tiroir, puis déclenche "theme:cart:change",
          // ce qui masque ce produit et affiche le suivant.
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        })
        .catch(function () {
          if (error) {
            error.textContent = '!';
            error.hidden = false;
          }
        })
        .finally(function () {
          button.disabled = false;
          item.classList.remove('is-loading');
        });
    }
  }

  customElements.define('cart-cross-sell', CartCrossSell);
})();